import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  type OnDestroy,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import type { ClassValue } from 'clsx';

import { buttonVariants, ButtonVariantType } from './button.variants';
import {
  ButtonVariant,
  ButtonVariantEnum,
  ButtonSeverity,
  ButtonSize,
  ButtonSizeEnum,
  ButtonShape,
  ButtonShapeEnum,
  ButtonIconPosition,
  ButtonIconPositionEnum,
  ButtonSeverityEnum,
} from './button.types';
import { mergeClasses, transform } from 'src/app/@core/utils/merge-classes';
import { IconComponent, IconSize, IconSizeEnum, IconSourceEnum, IconSource } from '../icon/icon.component';
import { ThrottleClickDirective } from 'src/app/@shared/directives/throttle-click.directive';
import { HlmButtonDirective } from '../../spartan/button/spartan-button.directive';

export type { ButtonVariant, ButtonSeverity, ButtonSize, ButtonShape, ButtonIconPosition };
export {
  ButtonVariantEnum,
  ButtonSizeEnum,
  ButtonShapeEnum,
  ButtonIconPositionEnum,
};

/**
 * Arena button wrapper (`app-button`).
 *
 * Renders a native `<button hlmBtn>` following the Spartan UI pattern.
 * `HlmButtonDirective` provides structural base classes (focus ring, disabled
 * pointer-events) while Arena CVA classes are passed via the `[class]` input —
 * Spartan's `hlm()` (twMerge) resolves any conflicts in our favour.
 *
 * ### Variant + Severity matrix
 * | variant    | severity  | resolved type          |
 * |------------|-----------|------------------------|
 * | filled     | primary   | primary                |
 * | outlined   | primary   | primary-outlined       |
 * | ghost      | primary   | primary-ghost          |
 * | filled     | danger    | danger                 |
 * | outlined   | danger    | danger-outlined        |
 * | (any)      | (omitted) | variant as type        |
 * | (omitted)  | (any)     | severity as type       |
 * | (omitted)  | (omitted) | default                |
 */
@Component({
  selector: 'app-button',
  exportAs: 'appButton',
  standalone: true,
  imports: [HlmButtonDirective, IconComponent, ThrottleClickDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button #innerBtn
      hlmBtn
      appThrottleClick
      [class]="arenaClasses()"
      [disabled]="disabled() || null"
      [throttleTime]="throttleClickTime()"
      [attr.data-icon-only]="iconOnly() || null"
      [attr.data-disabled]="disabled() || null"
      (click)="handleClick($event)"
      (throttledClick)="handleThrottledClick($event)">

      @if (!loading() && icon() && iconPosition() === IconPositionEnum.LEFT) {
        <app-icon [type]="icon()" [source]="iconSource()" [size]="iconSize()" />
      }
      @if (loading()) {
        <app-icon [type]="'loader-circle'" [source]="'lucide'" [size]="iconSize()"
          [class]="'animate-spin'" />
      }
      @if (label()) {
        <span class="truncate min-w-0">{{ label() }}</span>
      }
      <ng-content />
      @if (!loading() && icon() && iconPosition() === IconPositionEnum.RIGHT) {
        <app-icon [type]="icon()" [source]="iconSource()" [size]="iconSize()" />
      }

    </button>
  `,
  host: {
    '[attr.data-disabled]': 'disabled() || null',
    '[attr.disabled]': 'disabled() ? "" : null',
  },
})
export class ButtonComponent implements OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  protected readonly IconPositionEnum = ButtonIconPositionEnum;

  // ── Inner button reference (for icon-only detection) ───────────────────
  private readonly _innerBtn = viewChild<ElementRef<HTMLButtonElement>>('innerBtn');

  // ── Size, shape, layout ────────────────────────────────────────────────
  readonly size = input<ButtonSize>(ButtonSizeEnum.DEFAULT);
  readonly shape = input<ButtonShape>(ButtonShapeEnum.DEFAULT);
  readonly fullWidth = input(false, { transform });
  readonly loading = input(false, { transform });
  readonly disabled = input(false, { transform });
  readonly class = input<ClassValue>('');

  // ── Throttle ───────────────────────────────────────────────────────────
  readonly throttleClickTime = input<number>(1000);
  readonly enableThrottle = input(false, { transform });

  // ── Tooltip (consumed externally via PrimeNG pTooltip) ────────────────
  readonly toolTip = input<string>('');
  readonly toolTipPosition = input<'top' | 'bottom' | 'left' | 'right'>('top');

  // ── Icon ───────────────────────────────────────────────────────────────
  readonly icon = input<string>('');
  readonly iconPosition = input<ButtonIconPosition>(ButtonIconPositionEnum.LEFT);
  readonly iconSource = input<IconSource>(IconSourceEnum.PRIMENG);
  readonly iconSize = input<IconSize>(IconSizeEnum.SM);

  // ── Variant / severity ─────────────────────────────────────────────────
  /** Visual style: filled | outlined | ghost | text | link | default */
  readonly variant = input<ButtonVariant | string>('');
  /** Colour family: primary | secondary | contrast | danger | success | theme */
  readonly severity = input<ButtonSeverity | string>(ButtonSeverityEnum.CONTRAST);
  /** Button label text */
  readonly label = input<string>('');

  // ── Output ─────────────────────────────────────────────────────────────
  readonly clicked = output<Event>();

  // ── Icon-only detection ────────────────────────────────────────────────
  private readonly iconOnlyState = signal(false);
  readonly iconOnly = this.iconOnlyState.asReadonly();

  private _mutationObserver: MutationObserver | null = null;

  constructor() {
    afterNextRender(() => {
      const target = this._innerBtn()?.nativeElement ?? this.elementRef.nativeElement;

      const check = () => {
        const hasIcon = target.querySelector('app-icon') !== null;
        const children = Array.from<Node>(target.childNodes);
        const hasText = children.some(node => {
          if (node.nodeType === Node.TEXT_NODE) {
            return (node.textContent?.trim() ?? '') !== '';
          }
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            if (el.matches('app-icon')) return false;
            return (el.textContent?.trim() ?? '') !== '';
          }
          return false;
        });
        this.iconOnlyState.set(hasIcon && !hasText);
      };

      check();
      this._mutationObserver = new MutationObserver(check);
      this._mutationObserver.observe(target, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    });
  }

  ngOnDestroy(): void {
    this._mutationObserver?.disconnect();
    this._mutationObserver = null;
  }

  // ── Arena class computation ────────────────────────────────────────────
  //
  // Passed as `[class]` to `<button hlmBtn>`, which routes through
  // HlmButtonDirective's `userClass` input into its `_computedClass`:
  //   hlm(spartanBaseClasses, ourArenaClasses)
  // twMerge inside hlm() keeps our Arena classes, discarding Spartan's
  // conflicting colour utilities (bg-primary, text-primary-foreground…).

  protected readonly arenaClasses = computed(() =>
    mergeClasses(
      buttonVariants({
        type: this._resolveType() as ButtonVariantType,
        size: this.size(),
        shape: this.shape(),
        full: this.fullWidth(),
        loading: this.loading(),
      }),
      'rounded-base',
      this.class(),
    )
  );

  /**
   * Resolves the CVA `type` key from `variant` + `severity`.
   *
   * | variant    | severity  | result                |
   * |------------|-----------|-----------------------|
   * | filled     | primary   | primary               |
   * | outlined   | primary   | primary-outlined      |
   * | ghost/text | primary   | primary-ghost         |
   * | filled     | danger    | danger                |
   * | outlined   | danger    | danger-outlined       |
   * | (any)      | —         | variant               |
   * | —          | (any)     | severity              |
   * | —          | —         | default               |
   */
  private _resolveType(): string {
    const variant = this.variant();
    const severity = this.severity();

    if (variant && severity) {
      if (variant === ButtonVariantEnum.OUTLINED) return `${severity}-outlined`;
      if (variant === ButtonVariantEnum.GHOST || variant === ButtonVariantEnum.TEXT) return `${severity}-ghost`;
      if (variant === ButtonVariantEnum.FILLED) return severity;
      return variant;
    }

    if (variant) return variant;
    if (severity) return severity;
    return ButtonVariantEnum.DEFAULT;
  }

  // ── Click handlers ─────────────────────────────────────────────────────

  protected handleClick(event: Event): void {
    if (!this.disabled() && !this.loading() && !this.enableThrottle()) {
      this.clicked.emit(event);
    }
  }

  protected handleThrottledClick(event: Event): void {
    if (!this.disabled() && !this.loading() && this.enableThrottle()) {
      this.clicked.emit(event);
    }
  }
}
